import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useErrorContext } from 'components/messages/ErrorBox';
import { usePageLoadingContext } from 'components/messages/PageLoading';
import { useLocale } from 'components/special/LanguageProvider';
import { apiGetCall, apiPostCall, isResultOk, saveFile } from 'components/special/apiCall';
import ClaimCalculateStatus from './../components/ClaimCalculateStatus';
import ActionHeader from './../components/ActionHeader';
import ActionFooter from '../components/ActionFooter';
import SubjectSearch from './../components/SubjectSearch';
import EditFormFieldTable from 'components/form/EditFormFieldTable';
import InputAmount from '../../components/input/InputAmount';
import InputDate from '../../components/input/InputDate';
import InputDropDownList from '../../components/input/InputDropDownList';
import InputRadioButtons from '../../components/input/InputRadioButtons';
import InputCheckBox from '../../components/input/InputCheckBox';
import InputText from '../../components/input/InputText';
import TextAmount from './../../components/text/TextAmount';
import Warning from 'jsx/elements/messages/Warning';

const OTHERWEB_URL = process.env.REACT_APP_ILWEB_URL ? process.env.REACT_APP_ILWEB_URL.replace(/\/$/, '') : '';
const BASE_URL = process.env.REACT_APP_ILWEB_API_URL;

export const Payment = ({history}) => {
  
  const params = new URLSearchParams(useLocation().search);  
  const setCheckErrorItems = useErrorContext();
  const setIsPageLoading = usePageLoadingContext();
  const { t } = useLocale();

  const [subjectList, setSubjectList] = useState([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [messageBox, setMessageBox] = useState({ showMessageBox : false,
                                                  messageBoxText : "",
                                                  messageBoxType : "" })

   //Init blank formdata record.
   const [formData, setFormData] = useState({
       isNew : true,
       claimID : null
    });  

  //Init blank working data record
  const [actionData, setActionData] = useState({
    claimNumber : "",
    signatures : [],
    lookup : {
        paymentTransactionKeys : [],
        discountCreditorTranscationKeys : [],
        discountLawyerTransactionKeys : [],
    },
    data : {
        allowPenaltyDiscount : false
    }
  });

  //Init blank perform action data record
  const [performActionData, setPerformActionData] = useState({
    paymentLines : [{transactionKeyID:0, amount:0, lawyerAmount:0},
                    {transactionKeyID:0, amount:0, lawyerAmount:0},
                    {transactionKeyID:0, amount:0, lawyerAmount:0},
                    {transactionKeyID:0, amount:0, lawyerAmount:0},
                    {transactionKeyID:0, amount:0, lawyerAmount:0}],
    actionDate : new Date(),
    signatureID : null,
    formID : null
  });

  //Init blank calculation data record
  const [calculationData, setCalculationData] = useState({
    specialReminder: 0,
    totalAmountReminderCalculated: 0,
    lawyerAmountReminderCalculated: 0,
    creditorDiscountReminderCalculated: 0,
    lawyerDiscountReminderCalculated: 0,

    penaltyCreditorDiscountReminderCalculated: 0,
    penaltyLawyerDiscountReminderCalculated: 0,

    totalAmountReminder: 0,
    lawyerAmountReminder: 0,
    discountCreditorReminder: 0,
    discountLaywerReminder: 0
  });

  const updatePaymentLines = (index, paymentLine) => {
    const newPaymentLines = [...performActionData.paymentLines];
    newPaymentLines[index] = paymentLine;
    setPerformActionData({...performActionData, paymentLines: newPaymentLines}); 
  }

  const goBack = () => {
      //Go back to the main claim detail window.
    if (!formData.claimID){
        history.goBack();
    }
    else{
        window.location.assign(OTHERWEB_URL + "/claim-detail/"+ formData.claimID);
    }    
  }

  const loadFormData = async() => {
    setIsPageLoading(true);
    let newFormData = {...formData};    
    //Read the query parameters, if we don´t have any then go back  
    newFormData.claimID = params.get("claimid");    
    if (newFormData.claimID == null || newFormData.claimID <= 0){      
        goBack();
    }
    //Load action data.
    let requestResult = await apiGetCall(BASE_URL + '/action/init/payment/' + newFormData.claimID);       
    if (isResultOk(requestResult, setCheckErrorItems)){        
      setActionData(requestResult);
      setPerformActionData(requestResult.actionData);
      handleQueryInput({ input: requestResult.data.payerRegno });
    }
    setFormData(newFormData);
    setIsPageLoading(false);
  }

  const calculateClaimRemainder = async() => {
    if (performActionData && actionData && actionData.claimNumber != ""){
      let paymentLines = [];
      let line1 = performActionData.paymentLines[0];
      let newLine1 = { ...line1 };
      if (line1.transactionKeyID && line1.transactionKeyID != null && line1.amount > 0){
        newLine1.amount = newLine1.amount - newLine1.lawyerAmount;
      }
      paymentLines.push(newLine1);
      for (let i = 1; i < performActionData.paymentLines.length; i++){
        const e = performActionData.paymentLines[i];
        if (e.transactionKeyID && e.transactionKeyID != null && e.amount > 0){
          paymentLines.push(e);
        }
      }
      for (let i = 0; i < paymentLines.length; i++){
        if (paymentLines[i].amount == null){
          paymentLines[i].amount = 0;
        }
        if (paymentLines[i].lawyerAmount == null){
          paymentLines[i].lawyerAmount = 0;
        }
      }
      let request = {
        claimID : actionData.claimID,
        paymentDate : performActionData.actionDate,
        paymentLines : paymentLines,
        principalsTo : performActionData.principalsTo,
        calledIn : performActionData.calledInTypeID == 1
      };
      setIsPageLoading(true);
      let requestResult = null;
      requestResult = await apiPostCall(BASE_URL + '/claim/calculate/payment/estimation', request);
      if (isResultOk(requestResult, setCheckErrorItems)){
        let creditorTotal = requestResult.remainderSum.total;
        if (performActionData.calledInTypeID == 3){
          creditorTotal = creditorTotal + requestResult.currentSum.notCalledInRemainderWithInterest;
        }
        let creditorTotalWithOverPay = creditorTotal - requestResult.currentSum.paymentsCreditorOverPaid;
        let lawyerTotalWithOverPay = requestResult.remainderSum.lawyerTotalAmount - requestResult.currentSum.paymentsLawyerOverPaid;
        setCalculationData({
          totalAmountReminderCalculated: creditorTotal,
          lawyerAmountReminderCalculated: requestResult.remainderSum.lawyerTotalAmount,
          creditorDiscountReminderCalculated: creditorTotal - requestResult.remainderSum.lawyerTotalAmount,
          lawyerDiscountReminderCalculated: requestResult.remainderSum.lawyerTotalAmount,
          penaltyCreditorDiscountReminderCalculated: requestResult.remainderSum.penaltyInterests,
          penaltyLawyerDiscountReminderCalculated: requestResult.remainderSum.totalCostPenaltyInterests,
          specialReminder: creditorTotalWithOverPay - lawyerTotalWithOverPay
        });
      }
      setIsPageLoading(false);
    }
  }

  const onBondCalculationChange = (value) => {
    if (value == 1){
      setPerformActionData({...performActionData, principalsTo: actionData.data.calledInDate, calledInTypeID : value});
    }else{
      setPerformActionData({...performActionData, principalsTo: performActionData.actionDate, calledInTypeID : value});
    }
  }

  
  useEffect(() => {
    loadFormData();
  }, []);

  useEffect(() => {
    calculateClaimRemainder();
  }, [performActionData.calledInTypeID]);

  useEffect(() => {
    if (subjectList.length > 0) {
      let subject = subjectList[0];
      querySelection({ item: subject });
      // itemSelection(subject);
      queryInputValueUpdate(subject.source.subjectName);
      // queryInputValueUpdate(subject.name);
    }
  }, [subjectList]);


  const onSaveClick = async() => {
    if (!validateInput()){
      return;
    }
    setIsPageLoading(true);
    let hasPaymentDiscount = false; // Not sure what this is telling me?
    let theData = performActionData;
    theData.paymentLines[0].amount = theData.paymentLines[0].amount - theData.paymentLines[0].lawyerAmount;
    if (theData.paymentLines[1].amount > 0 || theData.paymentLines[2].amount > 0){
      hasPaymentDiscount = true;
    }
    let requestResult = null;        
    requestResult = await apiPostCall(BASE_URL + '/action/perform/payment', performActionData);           
    if (isResultOk(requestResult, setCheckErrorItems)){          
      if (requestResult.letterDocument){
        saveFile(requestResult.letterDocument, requestResult.letterFileName, "application/rtf;charset=utf-8");
      }
      goBack();
    }
    setIsPageLoading(false);    
  }

  const onBackClick = () => {    
    goBack();
  }

  function triggerMessageBox(text, type) {
    setMessageBox({ showMessageBox : true,
      messageBoxText : text,
      messageBoxType : type });
  }

  const validateInput = () => {
    let valueExists = false;
    for (let i = 1; i < performActionData.paymentLines.length; i++){
      const e = performActionData.paymentLines[i];
      if (e.transactionKeyID && e.transactionKeyID != null && (e.amount > 0 || e.lawyerAmount > 0)){
        valueExists = true;
      }
    }
    if (!performActionData.payerSubjectID || !performActionData.payerName){
      triggerMessageBox('Vinsamlegast veljið "Greiðanda"', "alert");
      return false;
    }
    if (!valueExists){
      let selectedTransactionKey = actionData.lookup.paymentTransactionKeys.find(item => item.itemID == performActionData.paymentLines[0].transactionKeyID);
      if (!performActionData.paymentLines[0].transactionKeyID){
        triggerMessageBox('Vinsamlegast veljið "Tegund"', "alert");
        return false;
      }
      if (performActionData.paymentLines[0].amount == undefined) {
        triggerMessageBox('Vinsamlegast skráið inn "' + selectedTransactionKey.name + '".', "alert");
        return false;
      }
      if ((calculationData.totalAmountReminder - performActionData.paymentLines[0].amount) < 0) {
        triggerMessageBox('Eftistöðvar fyrir "' + selectedTransactionKey.name + '" mega ekki vera neikvæðar.', "alert");
        return false;
      }
      if (performActionData.paymentLines[0].lawyerAmount == undefined) {
        triggerMessageBox('Vinsamlegast skráið inn "Hluti lögmanns"', "alert");
        return false;
      }
      if ((performActionData.paymentLines[0].amount - performActionData.paymentLines[0].lawyerAmount) < 0) {
        triggerMessageBox('Hluti lögmanns má ekki vera meiri en sjálf innborgun.', "alert");
        return false;
      }
      if ((calculationData.lawyerAmountReminder - performActionData.paymentLines[0].lawyerAmount) < 0) {
        triggerMessageBox('Eftistöðvar fyrir "Hlut lögmanns" mega ekki vera neikvæðar.', "alert");
        return false;
      }
      if(performActionData.paymentLines[0].amount === 0 && performActionData.paymentLines[1].amount === 0 && performActionData.paymentLines[2].amount === 0){
        triggerMessageBox('Engin upphæð er skráð til innborgunar', "alert");
        return false;
      }
    }
    return true; 
  }

  const handleQueryInput = async(input) => {
    let searchParam = {
      query: input ? input.input : "",
      pageSize: 30,
      pageNumber: 0,
      filter : [],
      orderBy : [],
      restrictions : []
    };    

    searchParam.orderBy.push({
      field : 'subjectName.raw',
      order : 'Asc'
    }); 
    setIsSearchLoading(true);
    let requestResult = await apiPostCall(BASE_URL + '/search/subject', searchParam); 
    if (isResultOk(requestResult, setCheckErrorItems)){  
      setIsSearchLoading(false);      
      setSubjectList(requestResult.result.hits.hits);           
    }    
  }

  const queryInputValueUpdate = (value) => {
  }

  const querySelection = async(item) => {
    if (item){
      let subjectID = item.item.source.subjectID;
      if ((!subjectID) || (subjectID == null) || (subjectID === 0)){
        let requestResult = await apiGetCall(`${BASE_URL}/subject/create/${item.item.source.regno}`);
        if (isResultOk(requestResult, setCheckErrorItems)){
          setPerformActionData({...performActionData, payerSubjectID: requestResult.subjectID, payerName: requestResult.name});
        }else{
          setSubjectList(null);
          setPerformActionData({...performActionData, payerSubjectID: null, payerName: null});
        }
      }else{
        setPerformActionData({...performActionData, payerSubjectID: item.item.source.subjectID, payerName: item.item.source.subjectName});
      }
    }
  }

  const updateSkipLetter = (value) => {
    setPerformActionData({...performActionData, skipLetter: value});
  }

  return (
    <div>
      <ActionHeader title={t("Innborgun")} claimNumber={actionData.claimNumber}/>
      <EditFormFieldTable fields={[
        {
          title: t("Greiðandi"),
          edit: () => <SubjectSearch 
                      querySelectorInput={handleQueryInput}
                      valueUpdate={queryInputValueUpdate}
                      placement="bottom"
                      triggerIconClass="icon-magnifingglass-left"
                      listData={subjectList}
                      nameObject={['source.subjectName','source.regno','source.legalFormName']}
                      placeholder="kennitala eða nafn"
                      selection={querySelection}
                      initialItem="0"
                      isLoading={isSearchLoading}/>
        },
        {
          title: t("Dags"),
          edit: () => <div>
                        <InputDate value={performActionData.actionDate} onChange={(value) => setPerformActionData({...performActionData, actionDate : value})} disabled={false}/>
                        <div className="col s1of1 form text-right">
                            <label>{t("Eftirstöðvar kröfu")}</label>
                        </div>
                      </div>
        },
        {
          title: () =>  <div className="form">
                          <InputDropDownList
                            list={actionData.lookup.paymentTransactionKeys}
                            keyField="itemID"
                            displayField = "name"
                            defaultValue = {performActionData.paymentLines[0].transactionKeyID}
                            onChange={(value) => updatePaymentLines(0, {...performActionData.paymentLines[0], transactionKeyID : value})}
                            nullable={false}
                            disabled={false}/>
                        </div>,
          edit: () => <div className="grid tight collapse840">
                        <div className="col s4of5">
                          <InputAmount value={performActionData.paymentLines[0].amount} onChange={(value) => updatePaymentLines(0, {...performActionData.paymentLines[0], amount : value})} onBlur={calculateClaimRemainder} placeholder={t("Upphæð")} disabled={false} allowNegative={false}/>
                        </div>
                        <div className="col s1of5 text-right text" style={{paddingRight: '15px'}}>
                          <TextAmount value={calculationData.totalAmountReminderCalculated}/>
                        </div>
                      </div>
        },
        {
          title: () =>  <div className="grid tight collapse840" style={{paddingLeft: '10px'}}>
                          <div className="col s1of2">
                            {t("Hluti lögmanns")}
                          </div>
                          {
                            calculationData.specialReminder < 0 &&
                            <div className="col s1of2 text-right" style={{paddingRight: '15px'}}>
                              <TextAmount value={calculationData.specialReminder}/>
                            </div>
                          }
                        </div>,
          edit: () => <div className="grid tight collapse840">
                        <div className="col s4of5">
                          <InputAmount value={performActionData.paymentLines[0].lawyerAmount} onChange={(value) => updatePaymentLines(0, {...performActionData.paymentLines[0], lawyerAmount : value})} onBlur={calculateClaimRemainder} placeholder={t("Upphæð")} disabled={false} allowNegative={false}/>
                          </div>
                        <div className="col s1of5 text-right text" style={{paddingRight: '15px'}}>
                          <TextAmount value={calculationData.lawyerAmountReminderCalculated}/>
                        </div>
                      </div>
        },
        {
          title: () =>  <div className="form">
                          <InputDropDownList
                            list={actionData.lookup.discountCreditorTranscationKeys}
                            keyField="itemID"
                            displayField = "name"
                            defaultValue = {performActionData.paymentLines[1].transactionKeyID}
                            onChange={(value) => updatePaymentLines(1, {...performActionData.paymentLines[1], transactionKeyID : value})}
                            nullable={false}
                            disabled={false}/>
                        </div>,
          edit: () => <div className="grid tight collapse840">
                        <div className="col s4of5">
                          <InputAmount value={performActionData.paymentLines[1].amount} onChange={(value) => updatePaymentLines(1, {...performActionData.paymentLines[1], amount : value})} onBlur={calculateClaimRemainder} placeholder={t("Upphæð")} disabled={false} allowNegative={false}/>
                        </div>
                        <div className="col s1of5 text-right text" style={{paddingRight: '15px'}}>
                          <TextAmount value={calculationData.creditorDiscountReminderCalculated}/>
                        </div>
                      </div>
        },
        {
          title: () =>  <div className="form">
                          <InputDropDownList
                            list={actionData.lookup.discountLawyerTransactionKeys}
                            keyField="itemID"
                            displayField = "name"
                            defaultValue = {performActionData.paymentLines[2].transactionKeyID}
                            onChange={(value) => updatePaymentLines(2, {...performActionData.paymentLines[2], transactionKeyID : value})}
                            nullable={false}
                            disabled={false}/>
                        </div>,
            edit: () => <div className="grid tight collapse840">
                          <div className="col s4of5">
                            <InputAmount value={performActionData.paymentLines[2].amount} onChange={(value) => updatePaymentLines(2, {...performActionData.paymentLines[2], amount : value})} onBlur={calculateClaimRemainder} placeholder={t("Upphæð")} disabled={false} allowNegative={false}/>
                            </div>
                          <div className="col s1of5 text-right text" style={{paddingRight: '15px'}}>
                            <TextAmount value={calculationData.lawyerDiscountReminderCalculated}/>
                          </div>
                        </div>
        },
        (actionData.data.allowPenaltyDiscount) ? {
          title: () =>  <div className="form">
                          <InputDropDownList
                            list={actionData.lookup.penaltyDiscountCreditorTranscationKeys}
                            keyField="itemID"
                            displayField = "name"
                            defaultValue = {performActionData.paymentLines[3].transactionKeyID}
                            onChange={(value) => updatePaymentLines(3, {...performActionData.paymentLines[3], transactionKeyID : value})}
                            nullable={false}
                            disabled={false}/>
                        </div>,
            edit: () => <div className="grid tight collapse840">
                          <div className="col s4of5">
                            <InputAmount value={performActionData.paymentLines[3].amount} onChange={(value) => updatePaymentLines(3, {...performActionData.paymentLines[3], amount : value})} onBlur={calculateClaimRemainder} placeholder={t("Upphæð")} disabled={false} allowNegative={false}/>
                          </div>
                          <div className="col s1of5 text-right text" style={{paddingRight: '15px'}}>
                            <TextAmount value={calculationData.penaltyCreditorDiscountReminderCalculated}/>
                          </div>
                        </div>
        } : undefined,
        (actionData.data.allowPenaltyDiscount) ? {
          title: () =>  <div className="form">
                          <InputDropDownList
                            list={actionData.lookup.penaltyDiscountLawyerTransactionKeys}
                            keyField="itemID"
                            displayField = "name"
                            defaultValue = {performActionData.paymentLines[4].transactionKeyID}
                            onChange={(value) => updatePaymentLines(4, {...performActionData.paymentLines[4], transactionKeyID : value})}
                            nullable={false}
                            disabled={false}/>
                        </div>,
            edit: () => <div className="grid tight collapse840">
                          <div className="col s4of5">
                            <InputAmount value={performActionData.paymentLines[4].amount} onChange={(value) => updatePaymentLines(4, {...performActionData.paymentLines[4], amount : value})} onBlur={calculateClaimRemainder} placeholder={t("Upphæð")} disabled={false} allowNegative={false}/>
                          </div>
                          <div className="col s1of5 text-right text" style={{paddingRight: '15px'}}>
                            <TextAmount value={calculationData.penaltyLawyerDiscountReminderCalculated}/>
                          </div>
                        </div>
        } : undefined,
        (actionData.data.isBond) ? {
          title: t("Skuldabréf"),
          edit: () =>  <InputRadioButtons
                        list={actionData.lookup.calledInTypeList}
                        keyField="itemID"
                        displayField = "name"
                        defaultValue={performActionData.calledInTypeID}
                        onChange={(value) => onBondCalculationChange(value)}
                        disabled={false}/>
        } : undefined,
        (actionData.data.isBond) ? {
          title: t("Gjalddagar til"),
          edit: () => <InputDate value={performActionData.principalsTo} onChange={(value) => setPerformActionData({...performActionData, principalsTo : value})} disabled={false}/>
        } : undefined,
        (actionData.data.isBond) ? {
          title: t("Málinu skilað"),
          edit: () => <InputCheckBox value={performActionData.caseReturned} onChange={(value) => setPerformActionData({...performActionData, caseReturned : value})} disabled={false}/>
        } : undefined,
      ].filter(x => x !== undefined)}/>

      {formData.claimID && <ClaimCalculateStatus claimID={formData.claimID} hideButtons={true}/>}

      <div className="form">
        <label>{t("Skýring")}</label>
        <InputText value={performActionData.description} onChange={(value) => setPerformActionData({...performActionData, description : value })} disabled={false}/>
      </div>
      {messageBox.showMessageBox && <Warning messageHeader={messageBox.messageBoxText} onClose={() => setMessageBox({ ...messageBox, showMessageBox : false })} messageType={messageBox.messageBoxType}/>}
      <ActionFooter actioninit={actionData} performAction={performActionData} onCancel={onBackClick} onSubmit={onSaveClick} showSkipLetter={true} updateSkipLetter={updateSkipLetter}/>
    </div>
  );
}